import { type Ref, ref, watch } from 'vue';
import { toLogicalEventPoint } from '@/utils/screenRotation';

export interface EdgeDragDismissOptions {
  /**
   * Edge the panel is anchored to. A 'left' panel slides in from the left
   * edge, so only a left-ward (negative) drag moves it toward dismissal; a
   * 'right' panel is mirrored, only a right-ward (positive) drag counts.
   */
  anchor: 'left' | 'right';
  /** Drag distance, in px, past which release commits to closing. Default 80. */
  thresholdPx?: number;
  /**
   * Pointer/touch down on a matching target (e.g. a slider, a draggable
   * cell) opts out of the dismiss gesture entirely, so it doesn't fight that
   * control for the same drag. Defaults to never opting out.
   */
  isInteractiveTarget?: (target: EventTarget | null) => boolean;
}

/**
 * Drag-to-dismiss for a panel anchored to a screen edge (AnnotationPane's
 * left pane, GameAreaSettingsPane's right pane, MainNav's mobile menu) —
 * dragging it toward the opposite edge past `thresholdPx` closes it on
 * release; short of that, it springs back to `dragTranslateX` 0.
 *
 * Handles touch and mouse/pen via separate listeners (plain Touch Events for
 * touch, Pointer Events filtered to non-touch for mouse/pen) so desktop mouse
 * drags work the same way touch swipes do on mobile, without the two
 * interfering with each other. Spread the returned `handlers` onto the
 * draggable element with `v-on`.
 */
export function useEdgeDragDismiss(open: Ref<boolean>, options: EdgeDragDismissOptions) {
  const thresholdPx = options.thresholdPx ?? 80;
  // +1 for a 'right' panel (dismiss drag travels positive), -1 for 'left'.
  const sign = options.anchor === 'left' ? -1 : 1;
  const isInteractiveTarget = options.isInteractiveTarget ?? (() => false);

  const dragTranslateX = ref(0);
  const isDragging = ref(false);
  let dragStartX = 0;
  let currentTranslateX = 0;

  // Reset only when (re)opening, not when closing: closing via a committed
  // drag-dismiss leaves dragTranslateX already off-screen in the dismiss
  // direction, and the panel should keep departing that way while it
  // collapses/unmounts — resetting it to 0 here would make it visibly snap
  // back toward centre at the same time, fighting that exit animation.
  watch(open, (isOpen) => {
    if (isOpen) dragTranslateX.value = 0;
  });

  const beginDrag = (x: number) => {
    isDragging.value = true;
    dragStartX = x;
    currentTranslateX = dragTranslateX.value;
  };

  const updateDrag = (x: number) => {
    const diff = x - dragStartX;
    // Only allow dragging toward the dismiss direction for this anchor.
    if (diff * sign > 0) {
      dragTranslateX.value = currentTranslateX + diff;
    }
  };

  const endDrag = () => {
    if (!isDragging.value) return;
    isDragging.value = false;
    if (dragTranslateX.value * sign > thresholdPx) {
      open.value = false;
    } else {
      dragTranslateX.value = 0;
    }
  };

  // Touch path — kept as plain Touch Events rather than folded into the
  // Pointer Event handlers below: touch already reliably bubbles move/end to
  // this element regardless of where the finger travels, and mixing it with
  // pointer capture caused a real regression (mobile drag stopped working),
  // so touch and mouse/pen use fully separate listeners.
  const handleTouchStart = (e: TouchEvent) => {
    if (!open.value || isInteractiveTarget(e.target)) return;
    beginDrag(toLogicalEventPoint(e).x);
  };

  const handleTouchMove = (e: TouchEvent) => {
    if (!isDragging.value || !open.value) return;
    updateDrag(toLogicalEventPoint(e).x);
  };

  const handleTouchEnd = () => endDrag();

  // Mouse/pen path — Pointer Events, explicitly ignoring pointerType "touch"
  // so touch input is only ever handled once, above.
  const handlePointerDown = (e: PointerEvent) => {
    if (e.pointerType === 'touch' || !open.value || isInteractiveTarget(e.target)) return;
    beginDrag(toLogicalEventPoint(e).x);
    // Pointer capture keeps move/up events firing on this element even once
    // the pointer moves outside its bounds mid-drag — without it, a fast
    // desktop mouse drag would "escape" the panel and stop tracking.
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  };

  const handlePointerMove = (e: PointerEvent) => {
    if (e.pointerType === 'touch' || !isDragging.value || !open.value) return;
    updateDrag(toLogicalEventPoint(e).x);
  };

  const handlePointerUp = (e: PointerEvent) => {
    if (e.pointerType === 'touch') return;
    endDrag();
  };

  return {
    /** Current drag offset, in px; apply as `translateX(${dragTranslateX}px)`. */
    dragTranslateX,
    /** True for the duration of an active drag; use to suspend transitions. */
    isDragging,
    /** Spread onto the draggable element: `v-on="handlers"`. */
    handlers: {
      touchstart: handleTouchStart,
      touchmove: handleTouchMove,
      touchend: handleTouchEnd,
      pointerdown: handlePointerDown,
      pointermove: handlePointerMove,
      pointerup: handlePointerUp,
      pointercancel: handlePointerUp,
    },
  };
}
